import React, { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Menu, X, Phone, Mail, MapPin, Instagram, Facebook, 
  ChevronRight, Scissors, Palette, Printer, ShoppingBag, Sparkles,
  LogOut, Plus, Trash2, Edit, Save, Image as ImageIcon, ArrowLeft, Star, Quote
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_stitch-hub-28/artifacts/c7npy531_logo%20emmanuelle%20g.png";

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem("emmanuelle_token"));
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      await axios.get(`${API}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsAuthenticated(true);
    } catch {
      logout();
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const newToken = response.data.token;
    localStorage.setItem("emmanuelle_token", newToken);
    setToken(newToken);
    setIsAuthenticated(true);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("emmanuelle_token");
    setToken(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ token, isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Logo Component with uploaded image
const Logo = ({ light = false, size = "normal" }) => (
  <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
    <img 
      src={LOGO_URL} 
      alt="Emmanuelle G Logo" 
      className={`${size === "small" ? "w-10 h-10" : "w-14 h-14"} object-contain rounded-full`}
    />
    <div className="flex flex-col">
      <span className={`logo-text text-lg leading-tight ${light ? 'text-[#f5f5dc]' : 'text-[#004d40]'}`}>Emmanuelle G</span>
      <span className={`text-xs tracking-widest uppercase ${light ? 'text-[#f5f5dc]/70' : 'text-[#004d40]/60'}`}>Atelier</span>
    </div>
  </Link>
);

// Header Component
const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-[#004d40] ${
        scrolled ? 'py-3 shadow-lg' : 'py-5'
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <Logo light />
        
        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-10">
          <Link to="/" className="nav-link-light" data-testid="nav-home">Accueil</Link>
          <a href="/#services" className="nav-link-light" data-testid="nav-services">Services</a>
          <Link to="/portfolio" className="nav-link-light" data-testid="nav-portfolio">Portfolio</Link>
          <Link to="/contact" className="nav-link-light" data-testid="nav-contact">Contact</Link>
        </nav>

        {/* Mobile Menu Button */}
        <button 
          onClick={() => setIsOpen(!isOpen)} 
          className="md:hidden p-2 text-[#f5f5dc]"
          data-testid="mobile-menu-btn"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Nav */}
      {isOpen && (
        <nav className="md:hidden absolute top-full left-0 right-0 bg-[#004d40] border-t border-[#f5f5dc]/10 py-6 animate-fade-in">
          <div className="flex flex-col items-center gap-6">
            <Link to="/" className="nav-link-light" onClick={() => setIsOpen(false)} data-testid="mobile-nav-home">Accueil</Link>
            <a href="/#services" className="nav-link-light" onClick={() => setIsOpen(false)} data-testid="mobile-nav-services">Services</a>
            <Link to="/portfolio" className="nav-link-light" onClick={() => setIsOpen(false)} data-testid="mobile-nav-portfolio">Portfolio</Link>
            <Link to="/contact" className="nav-link-light" onClick={() => setIsOpen(false)} data-testid="mobile-nav-contact">Contact</Link>
          </div>
        </nav>
      )}
    </header>
  );
};

// Footer Component - Updated with WEBISULA and visible social icons
const Footer = () => (
  <footer className="bg-[#004d40] text-[#f5f5dc] py-16" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid md:grid-cols-3 gap-12 mb-12">
        <div>
          <Logo light />
          <p className="mt-4 text-[#f5f5dc]/70 text-sm leading-relaxed">
            Création à l'unité ou en série pour particuliers et professionnels. 
            Artisanat de qualité à Ajaccio, Corse.
          </p>
        </div>
        
        <div>
          <h4 className="text-sm uppercase tracking-widest mb-4 text-[#d4af37]">Contact</h4>
          <div className="space-y-3 text-sm text-[#f5f5dc]/80">
            <p className="flex items-center gap-3">
              <MapPin size={16} className="text-[#d4af37]" />
              Rue Paul Colonna d'Istria, 20090 Ajaccio
            </p>
            <p className="flex items-center gap-3">
              <Phone size={16} className="text-[#d4af37]" />
              06 20 90 06 17
            </p>
            <p className="flex items-center gap-3">
              <Mail size={16} className="text-[#d4af37]" />
              afec2a@gmail.com
            </p>
          </div>
        </div>
        
        <div>
          <h4 className="text-sm uppercase tracking-widest mb-4 text-[#d4af37]">Suivez-nous</h4>
          <div className="flex gap-4">
            <a 
              href="https://instagram.com/emmanuelle_g_2a" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-12 h-12 rounded-full bg-[#d4af37] flex items-center justify-center text-[#004d40] hover:bg-[#f5f5dc] transition-colors duration-300"
              data-testid="social-instagram"
            >
              <Instagram size={22} />
            </a>
            <a 
              href="https://facebook.com/EmmanuelleG" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-12 h-12 rounded-full bg-[#d4af37] flex items-center justify-center text-[#004d40] hover:bg-[#f5f5dc] transition-colors duration-300"
              data-testid="social-facebook"
            >
              <Facebook size={22} />
            </a>
          </div>
          
          {/* Google Reviews Link */}
          <a 
            href="https://www.google.com/search?sa=X&sca_esv=e9a141ab1f6f5f12&q=Emmanuelle+G+Avis&rflfq=1&num=20&stick=H4sIAAAAAAAAAONgkxI2MjIwtzC1tDQ3MzMxNTK3NLYw3MDI-IpR0DU3NzGvNDUnJ1XBXcGxLLN4ESumGADmpFBtQgAAAA&rldimm=2207859976645279381&tbm=lcl&hl=fr-FR&ved=2ahUKEwjapaP_6JyTAxVChP0HHQ4OHE0Q9fQKegQITBAG&biw=1470&bih=835&dpr=2#lkt=LocalPoiReviews"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 text-[#d4af37] hover:text-[#f5f5dc] transition-colors text-sm"
            data-testid="google-reviews-link"
          >
            <Star size={16} fill="currentColor" />
            Voir nos avis Google
          </a>
        </div>
      </div>
      
      <div className="border-t border-[#f5f5dc]/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-xs text-[#f5f5dc]/50">
          © {new Date().getFullYear()} Emmanuelle G. Tous droits réservés. Création{' '}
          <a 
            href="https://www.webisula.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[#d4af37] hover:text-[#f5f5dc] transition-colors"
            data-testid="webisula-link"
          >
            WEBISULA
          </a>
        </p>
        <Link to="/admin" className="text-xs text-[#f5f5dc]/30 hover:text-[#f5f5dc]/50" data-testid="admin-link">
          Administration
        </Link>
      </div>
    </div>
  </footer>
);

// Service Card Component
const ServiceCard = ({ icon: Icon, title, description, items }) => (
  <div className="service-card card-elegant group" data-testid={`service-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <Icon size={32} className="text-[#d4af37] mb-4" strokeWidth={1} />
    <h3 className="text-xl font-semibold mb-2">{title}</h3>
    <p className="text-[#5c6b63] text-sm mb-4">{description}</p>
    {items && (
      <ul className="text-xs text-[#5c6b63] space-y-1">
        {items.map((item, i) => (
          <li key={i} className="flex items-center gap-2">
            <ChevronRight size={12} className="text-[#d4af37]" />
            {item}
          </li>
        ))}
      </ul>
    )}
  </div>
);

// Progressive Image Component
const ProgressiveImage = ({ src, alt, className, ...props }) => {
  const [loaded, setLoaded] = React.useState(false);
  return (
    <div className="relative overflow-hidden bg-[#e8e8c8]/50">
      {!loaded && (
        <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-[#e8e8c8] via-[#f5f5dc] to-[#e8e8c8]" />
      )}
      <img
        src={src}
        alt={alt}
        className={`${className} transition-opacity duration-700 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setLoaded(true)}
        loading="lazy"
        {...props}
      />
    </div>
  );
};

// Photo Card Component - Improved visibility on hover
const PhotoCard = ({ photo, onClick }) => (
  <div 
    className="group cursor-pointer masonry-item"
    onClick={() => onClick(photo)}
    data-testid={`photo-${photo.id}`}
  >
    <div className="relative overflow-hidden rounded-sm shadow-md">
      <ProgressiveImage
        src={photo.image_data} 
        alt={photo.title}
        className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-105"
      />
      {/* Dark overlay for better text visibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#004d40]/90 via-[#004d40]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end justify-center">
        <div className="text-center p-6 w-full">
          <h4 className="font-serif text-xl text-[#f5f5dc] mb-2 drop-shadow-lg">{photo.title}</h4>
          {photo.description && (
            <p className="text-[#f5f5dc]/80 text-sm mb-2 line-clamp-2">{photo.description}</p>
          )}
          {photo.price && (
            <span className="inline-block bg-[#d4af37] text-[#004d40] px-4 py-1 rounded-full text-sm font-semibold">
              {photo.price}
            </span>
          )}
        </div>
      </div>
      {/* Category badge always visible */}
      <div className="absolute top-3 left-3">
        <span className="bg-[#004d40]/80 text-[#f5f5dc] text-xs uppercase tracking-wider px-3 py-1 rounded-full backdrop-blur-sm">
          {photo.category}
        </span>
      </div>
    </div>
  </div>
);

// Photo Modal Component
const PhotoModal = ({ photo, onClose }) => {
  if (!photo) return null;
  
  return (
    <div 
      className="fixed inset-0 z-50 bg-[#004d40]/90 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="photo-modal"
    >
      <div 
        className="max-w-4xl w-full bg-[#f5f5dc] rounded-sm overflow-hidden animate-fade-in-up"
        onClick={e => e.stopPropagation()}
      >
        <img 
          src={photo.image_data} 
          alt={photo.title}
          className="w-full h-auto max-h-[60vh] object-contain bg-[#e0e0d1]"
        />
        <div className="p-6">
          <div className="flex justify-between items-start mb-2">
            <h3 className="text-2xl font-serif">{photo.title}</h3>
            {photo.price && <span className="text-[#d4af37] font-semibold">{photo.price}</span>}
          </div>
          <p className="text-xs uppercase tracking-widest text-[#5c6b63] mb-2">{photo.category}</p>
          {photo.description && <p className="text-[#5c6b63]">{photo.description}</p>}
        </div>
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#f5f5dc] flex items-center justify-center"
          data-testid="close-modal-btn"
        >
          <X size={20} />
        </button>
      </div>
    </div>
  );
};

// Example photos for portfolio
const examplePhotos = [
  // Broderie
  {
    id: "ex-broderie-1",
    title: "Broderie florale traditionnelle",
    description: "Point de croix avec motif floral sur tissu blanc",
    category: "broderie",
    image_data: "https://images.unsplash.com/photo-1739127871640-044ef7c5f131?crop=entropy&cs=srgb&fm=jpg&q=55&w=480"
  },
  {
    id: "ex-broderie-2",
    title: "Cadre brodé personnalisé",
    description: "Broderie artisanale sur cadre ovale",
    category: "broderie",
    image_data: "https://images.unsplash.com/photo-1765212305522-3bf6adb8807d?crop=entropy&cs=srgb&fm=jpg&q=55&w=480"
  },
  {
    id: "ex-broderie-3",
    title: "Broderie pour bébé personnalisée",
    description: "Body brodé avec prénom et fleurs",
    category: "broderie",
    price: "35€",
    image_data: "https://images.unsplash.com/photo-1735269270590-d3eed08d3454?crop=entropy&cs=srgb&fm=jpg&q=55&w=480"
  },
  // Couture
  {
    id: "ex-couture-1",
    title: "Couture sur machine professionnelle",
    description: "Travail de précision sur textile bleu",
    category: "couture",
    image_data: "https://images.unsplash.com/photo-1625479142928-c2f2914318f2?crop=entropy&cs=srgb&fm=jpg&q=55&w=480"
  },
  {
    id: "ex-couture-2",
    title: "Atelier de couture",
    description: "Création et retouches sur-mesure",
    category: "couture",
    image_data: "https://images.unsplash.com/photo-1634626857321-deb416dcdb00?crop=entropy&cs=srgb&fm=jpg&q=55&w=480"
  },
  {
    id: "ex-couture-3",
    title: "Découpe et patronage",
    description: "Préparation des pièces pour création sur-mesure",
    category: "couture",
    image_data: "https://images.unsplash.com/photo-1771440048473-93e9171ad7d9?crop=entropy&cs=srgb&fm=jpg&q=55&w=480"
  },
  // Impressions
  {
    id: "ex-impressions-1",
    title: "Impression sérigraphie",
    description: "Impression textile professionnelle",
    category: "impressions",
    image_data: "https://images.unsplash.com/photo-1586939735437-d61392bd039b?crop=entropy&cs=srgb&fm=jpg&q=55&w=480"
  },
  {
    id: "ex-impressions-2",
    title: "Atelier d'impression numérique",
    description: "Personnalisation sur tous supports",
    category: "impressions",
    image_data: "https://images.unsplash.com/photo-1740126102414-fee092192258?crop=entropy&cs=srgb&fm=jpg&q=55&w=480"
  },
  {
    id: "ex-impressions-3",
    title: "Machine d'impression textile",
    description: "Équipement professionnel pour impressions de qualité",
    category: "impressions",
    image_data: "https://images.unsplash.com/photo-1693031630177-b897fb9d7154?crop=entropy&cs=srgb&fm=jpg&q=55&w=480"
  },
  // Créations
  {
    id: "ex-creations-1",
    title: "Création artisanale textile",
    description: "Travail à la main de tissus naturels",
    category: "creations",
    image_data: "https://images.unsplash.com/photo-1722963296065-d5f164b46d8c?crop=entropy&cs=srgb&fm=jpg&q=55&w=480"
  },
  {
    id: "ex-creations-2",
    title: "Pochettes et accessoires",
    description: "Créations sur-mesure en tissu",
    category: "creations",
    price: "25€",
    image_data: "https://images.unsplash.com/photo-1605489105705-5c504fa5cf92?crop=entropy&cs=srgb&fm=jpg&q=55&w=480"
  },
  {
    id: "ex-creations-3",
    title: "Sac tote bag artisanal",
    description: "Création unique en toile naturelle",
    category: "creations",
    price: "45€",
    image_data: "https://images.unsplash.com/photo-1629955539248-b138bde5de6f?crop=entropy&cs=srgb&fm=jpg&q=55&w=480"
  },
  // Ventes
  {
    id: "ex-ventes-1",
    title: "Boutique mode femme",
    description: "Articles de mode adultes disponibles",
    category: "ventes",
    image_data: "https://images.unsplash.com/photo-1760287363707-851f4780b98c?crop=entropy&cs=srgb&fm=jpg&q=55&w=480"
  },
  {
    id: "ex-ventes-2",
    title: "Vitrine de créations",
    description: "Nos pièces uniques en exposition",
    category: "ventes",
    image_data: "https://images.unsplash.com/photo-1763824969015-e5d1d6755782?crop=entropy&cs=srgb&fm=jpg&q=55&w=480"
  },
  {
    id: "ex-ventes-3",
    title: "Collection mode",
    description: "Vêtements et accessoires disponibles en boutique",
    category: "ventes",
    image_data: "https://images.unsplash.com/photo-1767334010488-83cdb8539273?crop=entropy&cs=srgb&fm=jpg&q=55&w=480"
  }
];

// Default reviews (fallback if database is empty)
const defaultReviews = [
  {
    id: "default-1",
    name: "Marie-Pierre D.",
    rating: 5,
    text: "Travail remarquable ! Emmanuelle a réalisé des broderies magnifiques pour ma liste de naissance. Je recommande vivement !",
    date: "Il y a 2 mois"
  },
  {
    id: "default-2",
    name: "Jean-François B.",
    rating: 5,
    text: "Impressions textiles de grande qualité pour mon entreprise. Service professionnel et rapide. Merci !",
    date: "Il y a 3 mois"
  },
  {
    id: "default-3",
    name: "Lucie M.",
    rating: 5,
    text: "Des créations uniques et un savoir-faire artisanal exceptionnel. Mon sac personnalisé est parfait !",
    date: "Il y a 1 mois"
  }
];

// Home Page
const HomePage = () => {
  const [photos, setPhotos] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [heroSettings, setHeroSettings] = useState({
    title_line1: "L'art de la",
    title_line2: "création",
    title_line3: "sur-mesure",
    subtitle: "Atelier de Couture - Ajaccio",
    description: "Créations uniques, broderie, impressions et personnalisation textile. Un savoir-faire artisanal au cœur de la Corse.",
    image_url: "https://images.unsplash.com/photo-1634626857321-deb416dcdb00?crop=entropy&cs=srgb&fm=jpg&q=55&w=480",
    event_banner: null,
    event_active: false
  });
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [loading, setLoading] = useState(true);

  const categories = [
    { id: "all", name: "Tout" },
    { id: "broderie", name: "Broderie" },
    { id: "couture", name: "Couture" },
    { id: "impressions", name: "Impressions" },
    { id: "creations", name: "Créations" },
    { id: "ventes", name: "Ventes" }
  ];

  const services = [
    {
      icon: Sparkles,
      title: "Créations & Personnalisation",
      description: "Pièces uniques et sur-mesure",
      items: ["Sacs, paniers, pochettes", "Accessoires femmes et enfants", "Listes de naissance"]
    },
    {
      icon: Printer,
      title: "Atelier d'Impressions",
      description: "Impression sur tous supports",
      items: ["Textiles, casquettes", "Verre, bois, cuir", "Porcelaine, plastique"]
    },
    {
      icon: Scissors,
      title: "Broderie & Flocage",
      description: "Personnalisation textile",
      items: ["Textiles, doudous", "Couvertures", "Objets divers"]
    },
    {
      icon: Palette,
      title: "Couture",
      description: "Création et retouches",
      items: ["Créations sur-mesure", "Retouches", "Transformations"]
    },
    {
      icon: ShoppingBag,
      title: "Ventes",
      description: "Boutique de créations",
      items: ["Mode adultes & enfants", "Objets décoratifs", "Pièces uniques"]
    }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [photosRes, reviewsRes, heroRes] = await Promise.all([
        axios.get(`${API}/photos`),
        axios.get(`${API}/reviews`),
        axios.get(`${API}/hero`)
      ]);
      
      // Use API photos, fallback to examples if empty
      const apiPhotos = photosRes.data;
      setPhotos(apiPhotos.length > 0 ? apiPhotos : examplePhotos);
      
      // Use API reviews, fallback to defaults if empty
      const apiReviews = reviewsRes.data;
      setReviews(apiReviews.length > 0 ? apiReviews : defaultReviews);
      
      // Hero settings
      if (heroRes.data) {
        setHeroSettings(heroRes.data);
      }
    } catch (error) {
      // Fallback to example data
      setPhotos(examplePhotos);
      setReviews(defaultReviews);
    } finally {
      setLoading(false);
    }
  };

  const filteredPhotos = activeCategory === "all" 
    ? photos 
    : photos.filter(p => p.category === activeCategory);

  return (
    <div className="texture-overlay" data-testid="home-page">
      {/* Event Banner */}
      {heroSettings.event_active && heroSettings.event_banner && (
        <div className="bg-[#d4af37] text-[#004d40] py-3 text-center font-semibold" data-testid="event-banner">
          {heroSettings.event_banner}
        </div>
      )}
      
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center pt-24">
          <div className="animate-fade-in-up">
            <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-4">{heroSettings.subtitle}</p>
            <h1 className="text-5xl lg:text-7xl font-serif leading-tight mb-6">
              {heroSettings.title_line1}<br />
              <span className="italic">{heroSettings.title_line2}</span><br />
              {heroSettings.title_line3}
            </h1>
            <p className="text-[#5c6b63] text-lg mb-8 max-w-md">
              {heroSettings.description}
            </p>
            <div className="flex gap-4 flex-wrap">
              <Link to="/contact" className="btn-primary" data-testid="hero-contact-btn">
                Nous contacter
              </Link>
              <Link to="/portfolio" className="btn-secondary" data-testid="hero-portfolio-btn">
                Voir nos créations
              </Link>
            </div>
          </div>
          <div className="relative animate-fade-in animation-delay-300">
            <div className="relative z-10">
              <ProgressiveImage
                src={heroSettings.image_url} 
                alt="Emmanuelle G - Atelier de Couture"
                className="w-full h-auto rounded-sm"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 w-48 h-48 border border-[#d4af37]/30 rounded-sm -z-0"></div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-[#e8e8c8]/30" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-4">Nos expertises</p>
            <h2 className="text-4xl lg:text-5xl font-serif">Services</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <ServiceCard key={index} {...service} />
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Preview Section */}
      <section id="galerie" className="py-24" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-4">Nos réalisations</p>
            <h2 className="text-4xl lg:text-5xl font-serif mb-8">Galerie</h2>
            
            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`text-xs uppercase tracking-widest px-4 py-2 transition-colors duration-300 ${
                    activeCategory === cat.id 
                      ? 'text-[#004d40] border-b-2 border-[#d4af37]' 
                      : 'text-[#5c6b63] hover:text-[#004d40]'
                  }`}
                  data-testid={`filter-${cat.id}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-[#5c6b63]">Chargement...</p>
            </div>
          ) : filteredPhotos.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPhotos.slice(0, 6).map(photo => (
                  <PhotoCard key={photo.id} photo={photo} onClick={setSelectedPhoto} />
                ))}
              </div>
              {filteredPhotos.length > 6 && (
                <div className="text-center mt-12">
                  <Link to="/portfolio" className="btn-secondary" data-testid="see-more-portfolio">
                    Voir tout le portfolio
                  </Link>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <ImageIcon size={48} className="mx-auto text-[#d4af37]/50 mb-4" />
              <p className="text-[#5c6b63]">Aucune photo dans cette catégorie pour le moment.</p>
            </div>
          )}
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="py-24 bg-[#e8e8c8]/30" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-4">Témoignages</p>
            <h2 className="text-4xl lg:text-5xl font-serif mb-4">Avis Google</h2>
            <div className="flex items-center justify-center gap-2 mb-8">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={24} className="text-[#d4af37] fill-[#d4af37]" />
              ))}
              <span className="ml-2 text-[#5c6b63]">5/5</span>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {reviews.map(review => (
              <div key={review.id} className="card-elegant relative" data-testid={`review-${review.id}`}>
                <Quote size={32} className="text-[#d4af37]/20 absolute top-4 right-4" />
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} size={16} className="text-[#d4af37] fill-[#d4af37]" />
                  ))}
                </div>
                <p className="text-[#5c6b63] mb-4 italic">"{review.text}"</p>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-sm">{review.name}</span>
                  <span className="text-xs text-[#5c6b63]">{review.date}</span>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <a 
              href="https://www.google.com/search?sa=X&sca_esv=e9a141ab1f6f5f12&q=Emmanuelle+G+Avis&rflfq=1&num=20&stick=H4sIAAAAAAAAAONgkxI2MjIwtzC1tDQ3MzMxNTK3NLYw3MDI-IpR0DU3NzGvNDUnJ1XBXcGxLLN4ESumGADmpFBtQgAAAA&rldimm=2207859976645279381&tbm=lcl&hl=fr-FR&ved=2ahUKEwjapaP_6JyTAxVChP0HHQ4OHE0Q9fQKegQITBAG&biw=1470&bih=835&dpr=2#lkt=LocalPoiReviews"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="see-all-reviews"
            >
              <Star size={16} />
              Voir tous les avis sur Google
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#004d40] text-[#f5f5dc]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-serif mb-6">Votre projet prend vie</h2>
          <p className="text-[#f5f5dc]/70 mb-8 max-w-2xl mx-auto">
            De l'idée à la réalisation, nous vous accompagnons dans tous vos projets de création textile.
            Particuliers ou professionnels, contactez-nous pour donner vie à vos envies.
          </p>
          <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 bg-[#f5f5dc] text-[#004d40] border-2 border-[#d4af37] rounded-full uppercase tracking-widest text-sm font-semibold hover:bg-[#d4af37] hover:text-[#004d40] transition-all duration-300 shadow-lg" data-testid="cta-contact-btn">
            Demander un devis
          </Link>
        </div>
      </section>

      <PhotoModal photo={selectedPhoto} onClose={() => setSelectedPhoto(null)} />
    </div>
  );
};

// Portfolio Page
const PortfolioPage = () => {
  const [photos, setPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [loading, setLoading] = useState(true);

  const categories = [
    { id: "all", name: "Tout" },
    { id: "broderie", name: "Broderie" },
    { id: "couture", name: "Couture" },
    { id: "impressions", name: "Impressions" },
    { id: "creations", name: "Créations" },
    { id: "ventes", name: "Ventes" }
  ];

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API}/photos`);
      const apiPhotos = response.data;
      // Use API photos, fallback to examples if empty
      setPhotos(apiPhotos.length > 0 ? apiPhotos : examplePhotos);
    } catch (error) {
      setPhotos(examplePhotos);
    } finally {
      setLoading(false);
    }
  };

  const filteredPhotos = activeCategory === "all" 
    ? photos 
    : photos.filter(p => p.category === activeCategory);

  return (
    <div className="texture-overlay pt-24" data-testid="portfolio-page">
      {/* Hero */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-4">Nos réalisations</p>
          <h1 className="text-5xl lg:text-6xl font-serif">Portfolio</h1>
          <p className="text-[#5c6b63] mt-4 max-w-2xl">
            Découvrez l'ensemble de nos créations : broderie, couture, impressions textiles et accessoires personnalisés.
          </p>
        </div>
      </section>

      {/* Gallery */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-4 mb-12">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`text-xs uppercase tracking-widest px-4 py-2 transition-colors duration-300 ${
                  activeCategory === cat.id 
                    ? 'text-[#004d40] border-b-2 border-[#d4af37]' 
                    : 'text-[#5c6b63] hover:text-[#004d40]'
                }`}
                data-testid={`portfolio-filter-${cat.id}`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-[#5c6b63]">Chargement...</p>
            </div>
          ) : filteredPhotos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredPhotos.map(photo => (
                <PhotoCard key={photo.id} photo={photo} onClick={setSelectedPhoto} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <ImageIcon size={48} className="mx-auto text-[#d4af37]/50 mb-4" />
              <p className="text-[#5c6b63]">Aucune photo dans cette catégorie.</p>
            </div>
          )}
        </div>
      </section>

      <PhotoModal photo={selectedPhoto} onClose={() => setSelectedPhoto(null)} />
    </div>
  );
};

// Contact Page
const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message envoyé avec succès !");
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Erreur lors de l'envoi du message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="texture-overlay pt-24" data-testid="contact-page">
      {/* Hero */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-4">Contactez-nous</p>
          <h1 className="text-5xl lg:text-6xl font-serif">Contact</h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div>
            <h2 className="text-2xl font-serif mb-8">Rendez-nous visite</h2>
            
            <div className="space-y-6 mb-12">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#004d40]/5 flex items-center justify-center flex-shrink-0">
                  <MapPin size={20} className="text-[#d4af37]" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Adresse</h3>
                  <p className="text-[#5c6b63]">Rue Paul Colonna d'Istria<br />20090 Ajaccio, Corse</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#004d40]/5 flex items-center justify-center flex-shrink-0">
                  <Phone size={20} className="text-[#d4af37]" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Téléphone</h3>
                  <a href="tel:0620900617" className="text-[#5c6b63] hover:text-[#004d40]" data-testid="phone-link">
                    06 20 90 06 17
                  </a>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#004d40]/5 flex items-center justify-center flex-shrink-0">
                  <Mail size={20} className="text-[#d4af37]" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Email</h3>
                  <a href="mailto:afec2a@gmail.com" className="text-[#5c6b63] hover:text-[#004d40]" data-testid="email-link">
                    afec2a@gmail.com
                  </a>
                </div>
              </div>
            </div>

            {/* Social Links - More Visible */}
            <div className="mb-12">
              <h3 className="font-semibold mb-4">Suivez-nous</h3>
              <div className="flex gap-4">
                <a 
                  href="https://instagram.com/emmanuelle_g_2a" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-4 py-3 bg-[#004d40] text-[#f5f5dc] rounded-full hover:bg-[#006055] transition-colors"
                  data-testid="contact-instagram"
                >
                  <Instagram size={20} />
                  <span className="text-sm">@emmanuelle_g_2a</span>
                </a>
                <a 
                  href="https://facebook.com/EmmanuelleG" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-4 py-3 bg-[#004d40] text-[#f5f5dc] rounded-full hover:bg-[#006055] transition-colors"
                  data-testid="contact-facebook"
                >
                  <Facebook size={20} />
                  <span className="text-sm">Emmanuelle G</span>
                </a>
              </div>
            </div>

            {/* Map */}
            <div className="rounded-sm overflow-hidden border border-[#004d40]/10">
              <a 
                href="https://www.google.com/maps/search/?api=1&query=Rue+Paul+Colonna+d'Istria+20090+Ajaccio"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="map-link"
              >
                <img 
                  src="https://images.unsplash.com/photo-1721943254338-a5805f698017?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" 
                  alt="Ajaccio, Corse"
                  className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                />
                <div className="p-4 bg-[#004d40]/5">
                  <p className="text-sm text-[#5c6b63] flex items-center gap-2">
                    <MapPin size={14} className="text-[#d4af37]" />
                    Voir sur Google Maps
                  </p>
                </div>
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <h2 className="text-2xl font-serif mb-8">Envoyez-nous un message</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#5c6b63] block mb-2">Nom *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    className="input-elegant"
                    placeholder="Votre nom"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#5c6b63] block mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})}
                    className="input-elegant"
                    placeholder="votre@email.com"
                    data-testid="input-email"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#5c6b63] block mb-2">Téléphone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={e => setFormData({...formData, phone: e.target.value})}
                    className="input-elegant"
                    placeholder="06 XX XX XX XX"
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#5c6b63] block mb-2">Sujet *</label>
                  <input
                    type="text"
                    required
                    value={formData.subject}
                    onChange={e => setFormData({...formData, subject: e.target.value})}
                    className="input-elegant"
                    placeholder="Demande de devis"
                    data-testid="input-subject"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-xs uppercase tracking-widest text-[#5c6b63] block mb-2">Message *</label>
                <textarea
                  required
                  rows={5}
                  value={formData.message}
                  onChange={e => setFormData({...formData, message: e.target.value})}
                  className="input-elegant resize-none"
                  placeholder="Décrivez votre projet..."
                  data-testid="input-message"
                />
              </div>
              
              <button 
                type="submit" 
                disabled={sending}
                className="btn-primary w-full disabled:opacity-50"
                data-testid="submit-contact-btn"
              >
                {sending ? "Envoi en cours..." : "Envoyer le message"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

// Admin Login Page
const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/admin/dashboard");
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
      toast.success("Connexion réussie");
      navigate("/admin/dashboard");
    } catch (error) {
      toast.error("Email ou mot de passe incorrect");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f5f5dc] px-6" data-testid="admin-login-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Logo" className="w-20 h-20 mx-auto rounded-full mb-4" />
          <h1 className="text-2xl font-serif">Administration</h1>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
          <div>
            <label className="text-xs uppercase tracking-widest text-[#5c6b63] block mb-2">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="input-elegant"
              placeholder="admin@email.com"
              data-testid="login-email"
            />
          </div>
          
          <div>
            <label className="text-xs uppercase tracking-widest text-[#5c6b63] block mb-2">Mot de passe</label>
            <input
              type="password"
              required
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="input-elegant"
              placeholder="••••••••"
              data-testid="login-password"
            />
          </div>
          
          <button 
            type="submit" 
            disabled={loading}
            className="btn-primary w-full disabled:opacity-50"
            data-testid="login-submit-btn"
          >
            {loading ? "Connexion..." : "Se connecter"}
          </button>
        </form>
        
        <Link to="/" className="flex items-center justify-center gap-2 mt-8 text-sm text-[#5c6b63] hover:text-[#004d40]" data-testid="back-to-site">
          <ArrowLeft size={16} />
          Retour au site
        </Link>
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const { token, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [messages, setMessages] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [heroSettings, setHeroSettings] = useState({
    title_line1: "L'art de la",
    title_line2: "création",
    title_line3: "sur-mesure",
    subtitle: "Atelier de Couture - Ajaccio",
    description: "Créations uniques, broderie, impressions et personnalisation textile. Un savoir-faire artisanal au cœur de la Corse.",
    image_url: "https://images.unsplash.com/photo-1634626857321-deb416dcdb00?crop=entropy&cs=srgb&fm=jpg&q=55&w=480",
    event_banner: "",
    event_active: false
  });
  const [activeTab, setActiveTab] = useState("accueil");
  const [showPhotoForm, setShowPhotoForm] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [editingReview, setEditingReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminCategoryFilter, setAdminCategoryFilter] = useState("all");
  const [seeding, setSeeding] = useState(false);
  const [savingHero, setSavingHero] = useState(false);
  
  const [photoForm, setPhotoForm] = useState({
    title: "",
    description: "",
    category: "creations",
    price: "",
    image_data: ""
  });

  const [reviewForm, setReviewForm] = useState({
    name: "",
    rating: 5,
    text: "",
    date: ""
  });

  const categories = [
    { id: "broderie", name: "Broderie" },
    { id: "couture", name: "Couture" },
    { id: "impressions", name: "Impressions" },
    { id: "creations", name: "Créations" },
    { id: "ventes", name: "Ventes" }
  ];

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [isAuthenticated, navigate]);

  const fetchData = async () => {
    try {
      const [photosRes, messagesRes, reviewsRes, heroRes] = await Promise.all([
        axios.get(`${API}/photos`),
        axios.get(`${API}/contact/messages`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/reviews`),
        axios.get(`${API}/hero`)
      ]);
      setPhotos(photosRes.data);
      setMessages(messagesRes.data);
      setReviews(reviewsRes.data);
      if (heroRes.data) {
        setHeroSettings({
          ...heroRes.data,
          event_banner: heroRes.data.event_banner || ""
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Hero handlers
  const handleHeroImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setHeroSettings({ ...heroSettings, image_url: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveHero = async () => {
    setSavingHero(true);
    try {
      await axios.put(`${API}/hero`, heroSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Page d'accueil mise à jour !");
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingHero(false);
    }
  };

  // Seed functions
  const handleSeedPhotos = async () => {
    setSeeding(true);
    try {
      const response = await axios.post(`${API}/seed/photos`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'ajout des photos");
    } finally {
      setSeeding(false);
    }
  };

  const handleSeedReviews = async () => {
    setSeeding(true);
    try {
      const response = await axios.post(`${API}/seed/reviews`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'ajout des avis");
    } finally {
      setSeeding(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoForm({ ...photoForm, image_data: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSavePhoto = async () => {
    if (!photoForm.title || !photoForm.image_data) {
      toast.error("Titre et image requis");
      return;
    }

    try {
      if (editingPhoto) {
        await axios.put(`${API}/photos/${editingPhoto.id}`, photoForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Photo mise à jour");
      } else {
        await axios.post(`${API}/photos`, photoForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Photo ajoutée");
      }
      
      setShowPhotoForm(false);
      setEditingPhoto(null);
      setPhotoForm({ title: "", description: "", category: "creations", price: "", image_data: "" });
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleEditPhoto = (photo) => {
    setEditingPhoto(photo);
    setPhotoForm({
      title: photo.title,
      description: photo.description || "",
      category: photo.category,
      price: photo.price || "",
      image_data: photo.image_data
    });
    setShowPhotoForm(true);
  };

  const handleDeletePhoto = async (id) => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    
    try {
      await axios.delete(`${API}/photos/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Photo supprimée");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleDeleteMessage = async (id) => {
    try {
      await axios.delete(`${API}/contact/messages/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Message supprimé");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Review handlers
  const handleSaveReview = async () => {
    if (!reviewForm.name || !reviewForm.text || !reviewForm.date) {
      toast.error("Nom, avis et date requis");
      return;
    }

    try {
      if (editingReview) {
        await axios.put(`${API}/reviews/${editingReview.id}`, reviewForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Avis mis à jour");
      } else {
        await axios.post(`${API}/reviews`, reviewForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Avis ajouté");
      }
      
      setShowReviewForm(false);
      setEditingReview(null);
      setReviewForm({ name: "", rating: 5, text: "", date: "" });
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleEditReview = (review) => {
    setEditingReview(review);
    setReviewForm({
      name: review.name,
      rating: review.rating,
      text: review.text,
      date: review.date
    });
    setShowReviewForm(true);
  };

  const handleDeleteReview = async (id) => {
    if (!window.confirm("Supprimer cet avis ?")) return;
    
    try {
      await axios.delete(`${API}/reviews/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Avis supprimé");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5dc]">
        <p className="text-[#5c6b63]">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5dc]" data-testid="admin-dashboard">
      {/* Admin Header */}
      <header className="bg-[#004d40] text-[#f5f5dc] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo light size="small" />
            <span className="text-xs uppercase tracking-widest opacity-70">Administration</span>
          </div>
          <button 
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm opacity-80 hover:opacity-100 transition-opacity"
            data-testid="logout-btn"
          >
            <LogOut size={16} />
            Déconnexion
          </button>
        </div>
      </header>

      {/* Admin Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-8 mb-8 border-b border-[#004d40]/10 overflow-x-auto">
          <button
            onClick={() => setActiveTab("accueil")}
            className={`pb-4 text-sm uppercase tracking-widest transition-colors whitespace-nowrap ${
              activeTab === "accueil" 
                ? "text-[#004d40] border-b-2 border-[#d4af37]" 
                : "text-[#5c6b63]"
            }`}
            data-testid="tab-accueil"
          >
            Accueil
          </button>
          <button
            onClick={() => setActiveTab("photos")}
            className={`pb-4 text-sm uppercase tracking-widest transition-colors whitespace-nowrap ${
              activeTab === "photos" 
                ? "text-[#004d40] border-b-2 border-[#d4af37]" 
                : "text-[#5c6b63]"
            }`}
            data-testid="tab-photos"
          >
            Photos ({photos.length})
          </button>
          <button
            onClick={() => setActiveTab("reviews")}
            className={`pb-4 text-sm uppercase tracking-widest transition-colors whitespace-nowrap ${
              activeTab === "reviews" 
                ? "text-[#004d40] border-b-2 border-[#d4af37]" 
                : "text-[#5c6b63]"
            }`}
            data-testid="tab-reviews"
          >
            Avis Google ({reviews.length})
          </button>
          <button
            onClick={() => setActiveTab("messages")}
            className={`pb-4 text-sm uppercase tracking-widest transition-colors whitespace-nowrap ${
              activeTab === "messages" 
                ? "text-[#004d40] border-b-2 border-[#d4af37]" 
                : "text-[#5c6b63]"
            }`}
            data-testid="tab-messages"
          >
            Messages ({messages.length})
          </button>
        </div>

        {/* Accueil Tab */}
        {activeTab === "accueil" && (
          <div>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
              <div>
                <h2 className="text-2xl font-serif">Page d'Accueil</h2>
                <p className="text-sm text-[#5c6b63] mt-1">Modifiez l'image principale et le texte de la page d'accueil</p>
              </div>
              <button 
                onClick={handleSaveHero}
                disabled={savingHero}
                className="bg-[#d4af37] text-[#004d40] px-6 py-3 rounded-full font-semibold uppercase tracking-wider text-sm hover:bg-[#c9a432] transition-colors flex items-center gap-2 shadow-lg"
                data-testid="save-hero-btn"
              >
                <Save size={20} />
                {savingHero ? "Sauvegarde..." : "Enregistrer"}
              </button>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Image Section */}
              <div className="card-elegant">
                <h3 className="text-lg font-serif mb-4">Image principale</h3>
                <div className="border-2 border-dashed border-[#004d40]/30 rounded-lg p-4 text-center mb-4">
                  <label className="cursor-pointer block">
                    {heroSettings.image_url ? (
                      <div className="relative">
                        <img 
                          src={heroSettings.image_url} 
                          alt="Image d'accueil" 
                          className="max-h-64 mx-auto rounded-sm object-cover"
                        />
                        <p className="text-sm text-[#5c6b63] mt-2">Cliquez pour changer l'image</p>
                      </div>
                    ) : (
                      <div className="py-8">
                        <ImageIcon size={48} className="mx-auto text-[#d4af37] mb-2" />
                        <p className="text-[#004d40] font-semibold">Cliquez pour sélectionner une image</p>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleHeroImageUpload}
                      className="hidden"
                      data-testid="hero-image-input"
                    />
                  </label>
                </div>
                <p className="text-xs text-[#5c6b63]">Ou entrez une URL d'image :</p>
                <input
                  type="text"
                  value={heroSettings.image_url}
                  onChange={e => setHeroSettings({...heroSettings, image_url: e.target.value})}
                  className="input-elegant text-sm"
                  placeholder="https://..."
                  data-testid="hero-image-url"
                />
              </div>

              {/* Text Section */}
              <div className="card-elegant">
                <h3 className="text-lg font-serif mb-4">Texte principal</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[#5c6b63] block mb-2">Sous-titre (en haut)</label>
                    <input
                      type="text"
                      value={heroSettings.subtitle}
                      onChange={e => setHeroSettings({...heroSettings, subtitle: e.target.value})}
                      className="input-elegant"
                      placeholder="Atelier de Couture - Ajaccio"
                      data-testid="hero-subtitle"
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[#5c6b63] block mb-2">Titre ligne 1</label>
                      <input
                        type="text"
                        value={heroSettings.title_line1}
                        onChange={e => setHeroSettings({...heroSettings, title_line1: e.target.value})}
                        className="input-elegant"
                        placeholder="L'art de la"
                        data-testid="hero-title1"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[#5c6b63] block mb-2">Titre ligne 2 <span className="text-[#d4af37]">(italique)</span></label>
                      <input
                        type="text"
                        value={heroSettings.title_line2}
                        onChange={e => setHeroSettings({...heroSettings, title_line2: e.target.value})}
                        className="input-elegant"
                        placeholder="création"
                        data-testid="hero-title2"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[#5c6b63] block mb-2">Titre ligne 3</label>
                      <input
                        type="text"
                        value={heroSettings.title_line3}
                        onChange={e => setHeroSettings({...heroSettings, title_line3: e.target.value})}
                        className="input-elegant"
                        placeholder="sur-mesure"
                        data-testid="hero-title3"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[#5c6b63] block mb-2">Description</label>
                    <textarea
                      rows={3}
                      value={heroSettings.description}
                      onChange={e => setHeroSettings({...heroSettings, description: e.target.value})}
                      className="input-elegant resize-none"
                      placeholder="Description de votre activité..."
                      data-testid="hero-description"
                    />
                  </div>
                </div>
              </div>

              {/* Event Banner Section */}
              <div className="card-elegant lg:col-span-2">
                <h3 className="text-lg font-serif mb-4">Bandeau événement spécial</h3>
                <p className="text-sm text-[#5c6b63] mb-4">Activez un bandeau en haut de la page pour les événements (soldes, fêtes, etc.)</p>
                
                <div className="flex items-center gap-4 mb-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={heroSettings.event_active}
                      onChange={e => setHeroSettings({...heroSettings, event_active: e.target.checked})}
                      className="w-5 h-5 accent-[#d4af37]"
                      data-testid="event-active-checkbox"
                    />
                    <span className="font-semibold">Activer le bandeau</span>
                  </label>
                </div>
                
                <input
                  type="text"
                  value={heroSettings.event_banner || ""}
                  onChange={e => setHeroSettings({...heroSettings, event_banner: e.target.value})}
                  className="input-elegant"
                  placeholder="Ex: ✨ Soldes d'hiver - Jusqu'à -30% sur une sélection d'articles !"
                  disabled={!heroSettings.event_active}
                  data-testid="event-banner-text"
                />
                
                {heroSettings.event_active && heroSettings.event_banner && (
                  <div className="mt-4 bg-[#d4af37] text-[#004d40] py-3 px-4 text-center font-semibold rounded-sm">
                    Aperçu : {heroSettings.event_banner}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Photos Tab */}
        {activeTab === "photos" && (
          <div>
            {/* Header with Add Button */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
              <div>
                <h2 className="text-2xl font-serif">Gestion des photos</h2>
                <p className="text-sm text-[#5c6b63] mt-1">Ajoutez vos créations, elles seront automatiquement classées par catégorie</p>
              </div>
              <div className="flex gap-3">
                {photos.length === 0 && (
                  <button 
                    onClick={handleSeedPhotos}
                    disabled={seeding}
                    className="bg-[#004d40]/10 text-[#004d40] px-4 py-3 rounded-full font-semibold uppercase tracking-wider text-xs hover:bg-[#004d40]/20 transition-colors flex items-center gap-2"
                    data-testid="seed-photos-btn"
                  >
                    {seeding ? "Chargement..." : "Charger exemples"}
                  </button>
                )}
                <button 
                  onClick={() => {
                    setEditingPhoto(null);
                    setPhotoForm({ title: "", description: "", category: "creations", price: "", image_data: "" });
                    setShowPhotoForm(true);
                  }}
                  className="bg-[#d4af37] text-[#004d40] px-6 py-3 rounded-full font-semibold uppercase tracking-wider text-sm hover:bg-[#c9a432] transition-colors flex items-center gap-2 shadow-lg"
                  data-testid="add-photo-btn"
                >
                  <Plus size={20} />
                  Publier une photo
                </button>
              </div>
            </div>

            {/* Category Filter for Admin */}
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={() => setAdminCategoryFilter("all")}
                className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider transition-colors ${
                  adminCategoryFilter === "all" 
                    ? "bg-[#004d40] text-[#f5f5dc]" 
                    : "bg-[#004d40]/10 text-[#004d40] hover:bg-[#004d40]/20"
                }`}
              >
                Toutes ({photos.length})
              </button>
              {categories.map(cat => {
                const count = photos.filter(p => p.category === cat.id).length;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setAdminCategoryFilter(cat.id)}
                    className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider transition-colors ${
                      adminCategoryFilter === cat.id 
                        ? "bg-[#004d40] text-[#f5f5dc]" 
                        : "bg-[#004d40]/10 text-[#004d40] hover:bg-[#004d40]/20"
                    }`}
                  >
                    {cat.name} ({count})
                  </button>
                );
              })}
            </div>

            {/* Photo Form Modal */}
            {showPhotoForm && (
              <div className="fixed inset-0 z-50 bg-[#004d40]/80 flex items-center justify-center p-4" data-testid="photo-form-modal">
                <div className="bg-[#f5f5dc] rounded-sm max-w-xl w-full max-h-[90vh] overflow-y-auto p-8">
                  <h3 className="text-xl font-serif mb-2">
                    {editingPhoto ? "Modifier la photo" : "Publier une nouvelle photo"}
                  </h3>
                  <p className="text-sm text-[#5c6b63] mb-6">La photo sera automatiquement classée dans la catégorie choisie</p>
                  
                  <div className="space-y-4">
                    {/* Image Upload First - More Prominent */}
                    <div className="border-2 border-dashed border-[#004d40]/30 rounded-lg p-6 text-center">
                      <label className="cursor-pointer block">
                        {photoForm.image_data ? (
                          <div className="relative">
                            <img 
                              src={photoForm.image_data} 
                              alt="Aperçu" 
                              className="max-h-48 mx-auto rounded-sm object-cover"
                            />
                            <p className="text-sm text-[#5c6b63] mt-2">Cliquez pour changer l'image</p>
                          </div>
                        ) : (
                          <div>
                            <ImageIcon size={48} className="mx-auto text-[#d4af37] mb-2" />
                            <p className="text-[#004d40] font-semibold">Cliquez pour sélectionner une image</p>
                            <p className="text-sm text-[#5c6b63]">JPG, PNG ou HEIC</p>
                          </div>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                          data-testid="photo-file-input"
                        />
                      </label>
                    </div>

                    <div>
                      <label className="text-xs uppercase tracking-widest text-[#5c6b63] block mb-2">Titre de la création *</label>
                      <input
                        type="text"
                        value={photoForm.title}
                        onChange={e => setPhotoForm({...photoForm, title: e.target.value})}
                        className="input-elegant"
                        placeholder="Ex: Sac brodé personnalisé"
                        data-testid="photo-title-input"
                      />
                    </div>
                    
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[#5c6b63] block mb-2">Catégorie (classement automatique)</label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {categories.map(cat => (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => setPhotoForm({...photoForm, category: cat.id})}
                            className={`p-3 rounded-lg text-xs uppercase tracking-wider transition-all ${
                              photoForm.category === cat.id 
                                ? "bg-[#004d40] text-[#f5f5dc] shadow-md" 
                                : "bg-[#004d40]/10 text-[#004d40] hover:bg-[#004d40]/20"
                            }`}
                            data-testid={`category-btn-${cat.id}`}
                          >
                            {cat.name}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[#5c6b63] block mb-2">Description (optionnel)</label>
                      <textarea
                        rows={3}
                        value={photoForm.description}
                        onChange={e => setPhotoForm({...photoForm, description: e.target.value})}
                        className="input-elegant resize-none"
                        placeholder="Décrivez votre création..."
                        data-testid="photo-description-input"
                      />
                    </div>
                    
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[#5c6b63] block mb-2">Prix (optionnel)</label>
                      <input
                        type="text"
                        value={photoForm.price}
                        onChange={e => setPhotoForm({...photoForm, price: e.target.value})}
                        className="input-elegant"
                        placeholder="Ex: 45€"
                        data-testid="photo-price-input"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-4 mt-8">
                    <button 
                      onClick={handleSavePhoto}
                      className="bg-[#d4af37] text-[#004d40] px-6 py-3 rounded-full font-semibold uppercase tracking-wider text-sm hover:bg-[#c9a432] transition-colors flex items-center justify-center gap-2 flex-1 shadow-lg"
                      data-testid="save-photo-btn"
                    >
                      <Save size={18} />
                      {editingPhoto ? "Mettre à jour" : "Publier"}
                    </button>
                    <button 
                      onClick={() => setShowPhotoForm(false)}
                      className="btn-secondary"
                      data-testid="cancel-photo-btn"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Photos Grid - Filtered */}
            {(() => {
              const filteredPhotos = adminCategoryFilter === "all" 
                ? photos 
                : photos.filter(p => p.category === adminCategoryFilter);
              
              return filteredPhotos.length > 0 ? (
                <div className="photo-grid-admin">
                  {filteredPhotos.map(photo => (
                    <div key={photo.id} className="card-elegant p-4" data-testid={`admin-photo-${photo.id}`}>
                      <div className="relative mb-4 overflow-hidden rounded-sm">
                        <img 
                          src={photo.image_data} 
                          alt={photo.title}
                          className="w-full h-48 object-cover"
                        />
                        <span className="absolute top-2 left-2 bg-[#004d40]/80 text-[#f5f5dc] text-xs uppercase tracking-wider px-2 py-1 rounded-full">
                          {photo.category}
                        </span>
                      </div>
                      <h4 className="font-serif text-lg mb-1">{photo.title}</h4>
                      {photo.price && (
                        <span className="inline-block bg-[#d4af37] text-[#004d40] text-xs px-3 py-1 rounded-full font-semibold mb-2">
                          {photo.price}
                        </span>
                      )}
                      {photo.description && <p className="text-sm text-[#5c6b63] mb-4 line-clamp-2">{photo.description}</p>}
                      
                      <div className="flex gap-2 mt-auto">
                        <button 
                          onClick={() => handleEditPhoto(photo)}
                          className="flex-1 btn-secondary py-2 text-xs flex items-center justify-center gap-1"
                          data-testid={`edit-photo-${photo.id}`}
                        >
                          <Edit size={14} />
                          Modifier
                        </button>
                        <button 
                          onClick={() => handleDeletePhoto(photo.id)}
                          className="px-4 py-2 text-red-600 border border-red-200 rounded-full text-xs hover:bg-red-50 transition-colors"
                          data-testid={`delete-photo-${photo.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <ImageIcon size={48} className="mx-auto text-[#d4af37]/50 mb-4" />
                  <p className="text-[#5c6b63]">
                    {adminCategoryFilter === "all" 
                      ? "Aucune photo pour le moment" 
                      : `Aucune photo dans la catégorie "${categories.find(c => c.id === adminCategoryFilter)?.name}"`
                    }
                  </p>
                  <p className="text-sm text-[#5c6b63]/70 mt-2">Cliquez sur "Publier une photo" pour ajouter une création !</p>
                </div>
              );
            })()}
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === "messages" && (
          <div>
            <h2 className="text-2xl font-serif mb-6">Messages reçus</h2>
            
            {messages.length > 0 ? (
              <div className="space-y-4">
                {messages.map(msg => (
                  <div key={msg.id} className="card-elegant" data-testid={`message-${msg.id}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="font-semibold">{msg.name}</h4>
                        <p className="text-sm text-[#5c6b63]">{msg.email}</p>
                        {msg.phone && <p className="text-sm text-[#5c6b63]">{msg.phone}</p>}
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-xs text-[#5c6b63]">
                          {new Date(msg.created_at).toLocaleDateString('fr-FR')}
                        </span>
                        <button 
                          onClick={() => handleDeleteMessage(msg.id)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                          data-testid={`delete-message-${msg.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs uppercase tracking-widest text-[#d4af37] mb-2">{msg.subject}</p>
                    <p className="text-[#5c6b63]">{msg.message}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Mail size={48} className="mx-auto text-[#d4af37]/50 mb-4" />
                <p className="text-[#5c6b63]">Aucun message pour le moment</p>
              </div>
            )}
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === "reviews" && (
          <div>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
              <div>
                <h2 className="text-2xl font-serif">Gestion des Avis Google</h2>
                <p className="text-sm text-[#5c6b63] mt-1">Ajoutez et modifiez les avis clients affichés sur le site</p>
              </div>
              <div className="flex gap-3">
                {reviews.length === 0 && (
                  <button 
                    onClick={handleSeedReviews}
                    disabled={seeding}
                    className="bg-[#004d40]/10 text-[#004d40] px-4 py-3 rounded-full font-semibold uppercase tracking-wider text-xs hover:bg-[#004d40]/20 transition-colors flex items-center gap-2"
                    data-testid="seed-reviews-btn"
                  >
                    {seeding ? "Chargement..." : "Charger exemples"}
                  </button>
                )}
                <button 
                  onClick={() => {
                    setEditingReview(null);
                    setReviewForm({ name: "", rating: 5, text: "", date: "" });
                    setShowReviewForm(true);
                  }}
                  className="bg-[#d4af37] text-[#004d40] px-6 py-3 rounded-full font-semibold uppercase tracking-wider text-sm hover:bg-[#c9a432] transition-colors flex items-center gap-2 shadow-lg"
                  data-testid="add-review-btn"
                >
                  <Plus size={20} />
                  Ajouter un avis
                </button>
              </div>
            </div>

            {/* Review Form Modal */}
            {showReviewForm && (
              <div className="fixed inset-0 z-50 bg-[#004d40]/80 flex items-center justify-center p-4" data-testid="review-form-modal">
                <div className="bg-[#f5f5dc] rounded-sm max-w-xl w-full max-h-[90vh] overflow-y-auto p-8">
                  <h3 className="text-xl font-serif mb-6">
                    {editingReview ? "Modifier l'avis" : "Ajouter un avis"}
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[#5c6b63] block mb-2">Nom du client *</label>
                      <input
                        type="text"
                        value={reviewForm.name}
                        onChange={e => setReviewForm({...reviewForm, name: e.target.value})}
                        className="input-elegant"
                        placeholder="Ex: Marie-Pierre D."
                        data-testid="review-name-input"
                      />
                    </div>
                    
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[#5c6b63] block mb-2">Note</label>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map(num => (
                          <button
                            key={num}
                            type="button"
                            onClick={() => setReviewForm({...reviewForm, rating: num})}
                            className="p-2"
                            data-testid={`rating-${num}`}
                          >
                            <Star 
                              size={24} 
                              className={num <= reviewForm.rating ? "text-[#d4af37] fill-[#d4af37]" : "text-[#5c6b63]"} 
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[#5c6b63] block mb-2">Texte de l'avis *</label>
                      <textarea
                        rows={4}
                        value={reviewForm.text}
                        onChange={e => setReviewForm({...reviewForm, text: e.target.value})}
                        className="input-elegant resize-none"
                        placeholder="Écrivez l'avis du client..."
                        data-testid="review-text-input"
                      />
                    </div>
                    
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[#5c6b63] block mb-2">Date *</label>
                      <input
                        type="text"
                        value={reviewForm.date}
                        onChange={e => setReviewForm({...reviewForm, date: e.target.value})}
                        className="input-elegant"
                        placeholder="Ex: Il y a 2 mois"
                        data-testid="review-date-input"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-4 mt-8">
                    <button 
                      onClick={handleSaveReview}
                      className="bg-[#d4af37] text-[#004d40] px-6 py-3 rounded-full font-semibold uppercase tracking-wider text-sm hover:bg-[#c9a432] transition-colors flex items-center justify-center gap-2 flex-1 shadow-lg"
                      data-testid="save-review-btn"
                    >
                      <Save size={18} />
                      {editingReview ? "Mettre à jour" : "Enregistrer"}
                    </button>
                    <button 
                      onClick={() => setShowReviewForm(false)}
                      className="btn-secondary"
                      data-testid="cancel-review-btn"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Reviews List */}
            {reviews.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {reviews.map(review => (
                  <div key={review.id} className="card-elegant relative" data-testid={`admin-review-${review.id}`}>
                    <Quote size={32} className="text-[#d4af37]/20 absolute top-4 right-4" />
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} size={16} className="text-[#d4af37] fill-[#d4af37]" />
                      ))}
                    </div>
                    <p className="text-[#5c6b63] mb-4 italic line-clamp-3">"{review.text}"</p>
                    <div className="flex justify-between items-center mb-4">
                      <span className="font-semibold text-sm">{review.name}</span>
                      <span className="text-xs text-[#5c6b63]">{review.date}</span>
                    </div>
                    
                    <div className="flex gap-2 pt-4 border-t border-[#004d40]/10">
                      <button 
                        onClick={() => handleEditReview(review)}
                        className="flex-1 btn-secondary py-2 text-xs flex items-center justify-center gap-1"
                        data-testid={`edit-review-${review.id}`}
                      >
                        <Edit size={14} />
                        Modifier
                      </button>
                      <button 
                        onClick={() => handleDeleteReview(review.id)}
                        className="px-4 py-2 text-red-600 border border-red-200 rounded-full text-xs hover:bg-red-50 transition-colors"
                        data-testid={`delete-review-${review.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Star size={48} className="mx-auto text-[#d4af37]/50 mb-4" />
                <p className="text-[#5c6b63]">Aucun avis pour le moment</p>
                <p className="text-sm text-[#5c6b63]/70 mt-2">Cliquez sur "Ajouter un avis" ou "Charger exemples" pour commencer</p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

// Layout Component
const Layout = ({ children }) => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  if (isAdmin) {
    return <>{children}</>;
  }

  return (
    <>
      <Header />
      <main>{children}</main>
      <Footer />
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Layout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/portfolio" element={<PortfolioPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
          </Routes>
        </Layout>
        <Toaster 
          position="bottom-right" 
          toastOptions={{
            style: {
              background: '#004d40',
              color: '#f5f5dc',
              border: 'none'
            }
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
